import { format, parse } from 'date-fns';
import _, { chain, forEach, isEmpty } from 'lodash';
import {
  DATE_FORMAT,
  DATE_FORMAT_SHORT,
  DATE_FORMAT_WITH_TIME,
} from '../constants';
import mapBackendErrorsToUserFields, {
  mapBackendErrorsToCalculatorFields,
} from '../validations/backendErrors';
import { PROPERTY_MAIN_RISKS, PROPERTY_RISKS } from '../constants/propertyCalcConstants';
import { usePurposes } from '../containers/PropertyCalculatorContainer/options';
import { PAYMENT_FREQUENCIES } from '../constants/paymentFrequency';
import { convertUserToApiFormat, convertUserToStoreFormat } from './contractHelpers';
import { convertToArm } from './dates';

const PROPERTY_TYPE_MAPPING = {
  1: 'apartment',
  2: 'house',
};

const PAYMENT_FREQUENCY_MAPPING = {
  1: 'single',
  2: 'quarterly',
  3: 'semiannual',
};

const USE_PURPOSE_MAPPING = {
  1: 'own',
  2: 'rent',
};

const RISK_MAPPING = {
  1: PROPERTY_RISKS.DESIGN_AND_ENGINEERING_EQUIPMENT,
  2: PROPERTY_RISKS.MOVABLE_PROPERTY,
  3: PROPERTY_RISKS.BEARING_STRUCTURES,
  4: PROPERTY_RISKS.CIVIL_LIABILITY,
};

export const propertyDetailsAreFilled = (propertyDetails) => {
  const { propertyType, propertyArea, usePurpose, paymentFrequency } = propertyDetails;
  return propertyType && propertyArea
    && usePurpose && usePurpose.value
    && paymentFrequency && paymentFrequency.value;
};

export const multiObjDiff = (a, b) => {
  const r = {};
  _.each(a, (v, k) => {
    r[k] = Object.fromEntries(Object.entries(v).filter(([key]) => !(key in b[k])));
  });
  return r;
};

export const convertPropertyContractToApiFormat = (contract) => {
  const signer = convertUserToApiFormat(contract.signer);

  const convertedContract = { ...signer };

  const { propertyDetails, propertyData, owners } = contract;
  const convertedOwners = owners.map((owner) => convertUserToApiFormat(owner));
  const {
    range,
    propertyType,
    usePurpose,
    propertyArea,
    paymentFrequency,
    amounts,
    extraRisks,
    paymentsSchedule,
    paymentFrequencies,
  } = propertyDetails;
  convertedContract.owners = convertedOwners;
  convertedContract.contractStart = range.start
      && format(convertToArm(range.start), DATE_FORMAT, new Date());
  convertedContract.contractEnd = range.end
      && format(convertToArm(range.end), DATE_FORMAT, new Date());
  convertedContract.propertyType = propertyType;
  convertedContract.purpose = usePurpose && usePurpose.value;
  convertedContract.propertyArea = propertyArea;
  convertedContract.paymentFrequency = paymentFrequency && paymentFrequency.value;
  convertedContract.amounts = amounts;
  convertedContract.risks = _.merge(PROPERTY_MAIN_RISKS, extraRisks);
  convertedContract.propertyData = propertyData;
  convertedContract.paymentsSchedule = paymentsSchedule;
  convertedContract.paymentFrequencies = paymentFrequencies;

  convertedContract.type = 'property';
  if (contract.draftId) {
    convertedContract.draftId = contract.draftId;
  }

  return convertedContract;
};

export const convertPropertyContractToStoreFormat = (
  contract,
  dateFormat = DATE_FORMAT,
  contractDateFormat = DATE_FORMAT,
) => {
  const {
    birthday,
    citizenship,
    firstName,
    lastName,
    firstNameEn,
    lastNameEn,
    gender,
    passport,
    passportGiven,
    passportValidUntil,
    passportFrom,
    resident,
    socCard,
    contactInfo,
    memberId,
  } = contract;

  const signer = convertUserToStoreFormat(
    {
      birthday,
      citizenship,
      firstName,
      lastName,
      firstNameEn,
      lastNameEn,
      gender,
      passport,
      passportGiven,
      passportValidUntil,
      passportFrom,
      resident,
      socCard,
      contactInfo,
      memberId,
    },
    dateFormat,
  );

  const owners = contract.owners.map((owner) => {
    const converted = convertUserToStoreFormat(owner, dateFormat);

    if (converted.memberId == null) {
      const isSigner = converted.passport === passport;

      // memberId can be 0 for signer
      converted.memberId = !isSigner ? converted.id : 0;
    }
    return converted;
  });

  const {
    propertyType,
    purpose,
    propertyArea,
    paymentFrequency,
    risks,
    contractStart,
    contractEnd,
    draftId,
    propertyData,
    paymentsSchedule,
    paymentFrequencies,
    amounts,
  } = contract;

  const propertyDetails = {
    propertyType,
    usePurpose: usePurposes.find((usePurpose) => usePurpose.value === purpose),
    propertyArea,
    paymentFrequency: PAYMENT_FREQUENCIES[paymentFrequency],
    range: {
      start: contractStart && parse(contractStart, contractDateFormat, new Date()),
      end: contractEnd && parse(contractEnd, contractDateFormat, new Date()),
    },
    birthday: signer.birthday,
    prices: {},
    amounts,
    paymentsSchedule,
    paymentFrequencies,
    extraRisks: multiObjDiff(risks, PROPERTY_MAIN_RISKS),
  };

  const hasAddedSigner = !!owners.find((owner) => owner.memberId === 0);

  return {
    signer,
    owners,
    propertyData,
    propertyDetails,
    draftId,
    hasAddedSigner,
  };
};

export const convertSignedPropertyContractToStoreFormat = (contract) => {
  const { properties, paymentFrequency } = contract;
  const propertyData = properties[0];

  const {
    propertyType,
    purpose,
    propertyArea,
    risks,
    propertyOwners,
  } = propertyData;

  const propertyRisks = {};

  risks.forEach((el) => {
    const key = RISK_MAPPING[el.risk];
    propertyRisks[key] = true;
  });

  const type = PROPERTY_TYPE_MAPPING[propertyType];

  const convertedContract = {
    ...contract,
    propertyData,
    risks: _.merge(PROPERTY_MAIN_RISKS, {
      [type]: propertyRisks,
    }),
    propertyArea,
    propertyType: type,
    purpose: USE_PURPOSE_MAPPING[purpose],
    paymentFrequency: PAYMENT_FREQUENCY_MAPPING[paymentFrequency],
    owners: propertyOwners,
  };

  return convertPropertyContractToStoreFormat(
    convertedContract,
    DATE_FORMAT_SHORT,
    DATE_FORMAT_WITH_TIME,
  );
};

// eslint-disable-next-line
export function mapErrorsToContractSteps(errors, contractData) {
  let signerBackendErrors;
  let calculatorBackendErrors;
  let nonPropertyErrors = [];

  // eslint-disable-next-line
  forEach(errors, (error, key) => {
    nonPropertyErrors.push(error);
  });
  // TODO: Add functionality like Travel

  nonPropertyErrors = chain(nonPropertyErrors)
    .flatten()
    .uniq()
    .value();

  signerBackendErrors = mapBackendErrorsToUserFields(nonPropertyErrors);
  calculatorBackendErrors = mapBackendErrorsToCalculatorFields(
    nonPropertyErrors,
  );

  if (isEmpty(signerBackendErrors)) {
    signerBackendErrors = null;
  }

  if (isEmpty(calculatorBackendErrors)) {
    calculatorBackendErrors = null;
  }

  return {
    signerBackendErrors,
    calculatorBackendErrors,
  };
}
